import { MRT_ColumnDef } from 'material-react-table';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { PermissionsEnum } from '@/auth/interfaces';
import {
  ROUTER_PATHS,
  TABLE_CONSTANTS_COLUMS,
  emptyCellOneLevel,
  formatCurrencyCell,
  formatDateWithTime,
  hasPermission,
  useCheckPermissions,
  useTableFilter,
  useTableServerSideFiltering,
} from '@/shared';
import {
  useFetchTipoInstalacion,
  useUpdateTipoInstalacion,
} from '@/shared/actions/erp/tipo-instalacion.actions';
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from '@/shared/components/ui';
import { TipoInstalacion } from '@/shared/interfaces';
import { useUiConfirmModalStore } from '@/store/ui';

const returnUrlTiposInstalacion =
  ROUTER_PATHS.administration.tiposInstalacionNav;

export interface TiposInstalacionesPageInterface { }

const TiposInstalacionesPage: React.FC<
  TiposInstalacionesPageInterface
> = () => {
  useCheckPermissions(PermissionsEnum.api_view_instalacion);

  const navigate = useNavigate();

  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  ///* global state
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );

  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* fetch data
  const {
    data: tiposInstalacionPagingRes,
    isLoading,
    isRefetching,
  } = useFetchTipoInstalacion({
    page: pageIndex + 1,
    page_size: pageSize,
    nombre: searchTerm,
    ...filterObject,
    filterByState: false,
  });

  ///* mutations
  const updateTipoInstalacionMutation = useUpdateTipoInstalacion(
    navigate,
    returnUrlTiposInstalacion
  );

  ///* handlers
  const onEdit = (original: TipoInstalacion) => {
    navigate(`${returnUrlTiposInstalacion}/editar/${original.id_instalacion}`);
  };

  ///* columns
  const columns = useMemo<MRT_ColumnDef<TipoInstalacion>[]>(
    () => [
      {
        accessorKey: 'nombre',
        header: 'Nombre',
        size: 300,
        Cell: ({ row }) => emptyCellOneLevel(row, 'nombre'),
      },
      {
        accessorKey: 'valor',
        header: 'Valor',
        size: 180,
        Cell: ({ row }) => formatCurrencyCell(row, 'valor'),
      },

      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          return (
            <SwitchComponente
              estadoSwitch={row.original.estado}
              titulo={row.original.estado ? 'Activo' : 'Inactivo'}
              setEstadoSwitch={speedState => {
                if (!hasPermission(PermissionsEnum.api_change_instalacion))
                  return;

                setConfirmDialog({
                  isOpen: true,
                  title: 'Cambiar Estado',
                  subtitle:
                    '¿Está seguro que desea cambiar el estado de este registro ?',
                  onConfirm: () => {
                    setConfirmDialogIsOpen(false);
                    updateTipoInstalacionMutation.mutate({
                      id: row.original.id_instalacion!,
                      data: { estado: !!speedState } as any,
                    });
                  },
                });
              }}
            />
          );
        },
      },
      {
        accessorKey: 'fecha_creacion',
        header: 'Fecha de Creación',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) =>
          row.original?.fecha_creacion
            ? formatDateWithTime(row.original.fecha_creacion)
            : 'N/A',
      },
      {
        accessorKey: 'fecha_modificacion',
        header: 'Fecha de Modificación',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) =>
          row.original?.fecha_modificacion
            ? formatDateWithTime(row.original.fecha_modificacion)
            : 'N/A',
      },
    ],
    []
  );

  return (
    <SingleTableBoxScene
      title="Tipos de Instalación"
      urlRedirect={`${returnUrlTiposInstalacion}/crear`}
      enableRedirect={hasPermission(PermissionsEnum.api_add_instalacion)}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        text="por nombre"
      />

      <GeneralTable<TipoInstalacion>
        columns={columns}
        data={tiposInstalacionPagingRes?.data?.data || []}
        //
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // search
        enableGlobalFilter={false}
        // // filters - llega al back
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={tiposInstalacionPagingRes?.data?.count}
        // // actions ========
        enableColumnActions={hasPermission(
          PermissionsEnum.api_change_instalacion
        )}
        actionsColumnSize={TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH}
        positionActionsColumn="first"
        // crud
        canEdit={hasPermission(PermissionsEnum.api_change_instalacion)}
        onEdit={onEdit}
        canDelete={false}
      />
    </SingleTableBoxScene>
  );
};

export default TiposInstalacionesPage;
